'use client';

import { captureException as capturePostHogException } from '@/lib/analytics/posthog';
import NextError from 'next/error';
import { useEffect } from 'react';

/**
 * Global error boundary for root layout
 * Catches errors in the root layout that aren't caught by regular error boundaries
 * 
 * Must include <html> and <body> tags per Next.js requirements
 * @see https://nextjs.org/docs/app/building-your-application/routing/error-handling#handling-errors-in-root-layouts
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Capture exception in PostHog with enhanced metadata
    capturePostHogException(error, {
      $exception_type: error.name,
      $exception_message: error.message,
      $exception_digest: error.digest,
      $exception_source: 'client',
      $exception_level: 'fatal', // Mark as fatal since it's root layout
      // Capture current page context
      $current_url: typeof window !== 'undefined' ? window.location.href : undefined,
      $pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });
  }, [error]);

  return (
    <html lang="he" dir="rtl">
      <body>
        {/* NextError is the default Next.js error page component */}
        <NextError statusCode={500} />
      </body>
    </html>
  );
}
